import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import logoIzyko from '../assets/logos/izyko-black.png';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Globe, Menu, X, User, LogOut } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../lib/utils';

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const languages = [
    { code: 'fr', name: 'Français' },
    { code: 'ht', name: 'Kreyòl' },
    { code: 'en', name: 'English' }
  ];

  return (
    <nav className="sticky top-0 z-50 glass border-b border-border/40 safe-top">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={logoIzyko} alt="izyko" className="h-12 md:h-13 w-auto object-contain" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-bold text-muted-foreground hover:text-foreground transition-colors">
              {t('nav.home')}
            </Link>
            <Link to="/pricing" className="text-sm font-bold text-muted-foreground hover:text-foreground transition-colors">
              {t('nav.pricing')}
            </Link>
            <Link to="/about" className="text-sm font-bold text-muted-foreground hover:text-foreground transition-colors">
              {t('nav.about')}
            </Link>
            <Link to="/contact" className="text-sm font-bold text-muted-foreground hover:text-foreground transition-colors">
              {t('nav.contact')}
            </Link>
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full" data-testid="language-selector">
                  <Globe className="h-4 w-4" strokeWidth={2} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl">
                {languages.map((lang) => (
                  <DropdownMenuItem 
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={cn("font-semibold rounded-lg", i18n.language === lang.code && "bg-accent")}
                  >
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-full gap-2" data-testid="user-menu-btn">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="h-3 w-3" strokeWidth={2} />
                    </div>
                    <span className="font-bold">{user?.first_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl w-48">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} className="font-semibold rounded-lg">
                    {t('nav.dashboard')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="font-semibold rounded-lg text-destructive">
                    <LogOut className="h-4 w-4 mr-2" strokeWidth={2} />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" className="rounded-full font-bold" asChild data-testid="login-btn">
                  <Link to="/login">{t('nav.login')}</Link>
                </Button>
                <Button className="rounded-full shadow-md" asChild data-testid="register-btn">
                  <Link to="/register">{t('nav.register')}</Link>
                </Button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-xl hover:bg-muted transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" strokeWidth={2} />
            ) : (
              <Menu className="h-6 w-6" strokeWidth={2} />
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border/40 animate-in slide-in-from-top-2">
            <div className="flex flex-col gap-1">
              <Link 
                to="/" 
                className="text-base font-bold py-3 px-4 rounded-xl hover:bg-muted transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.home')}
              </Link>
              <Link 
                to="/pricing" 
                className="text-base font-bold py-3 px-4 rounded-xl hover:bg-muted transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.pricing')}
              </Link>
              <Link 
                to="/about" 
                className="text-base font-bold py-3 px-4 rounded-xl hover:bg-muted transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.about')}
              </Link>
              <Link 
                to="/contact" 
                className="text-base font-bold py-3 px-4 rounded-xl hover:bg-muted transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.contact')}
              </Link>
              
              <div className="flex items-center gap-2 py-3 px-4">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={cn(
                      "text-sm px-4 py-2 rounded-full font-bold transition-colors",
                      i18n.language === lang.code ? "bg-primary text-primary-foreground" : "bg-muted"
                    )}
                  >
                    {lang.code.toUpperCase()}
                  </button>
                ))}
              </div>

              {isAuthenticated ? (
                <>
                  <Link 
                    to="/dashboard" 
                    className="text-base font-bold py-3 px-4 rounded-xl hover:bg-muted transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.dashboard')}
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="text-base font-bold py-3 px-4 rounded-xl hover:bg-muted transition-colors text-left text-destructive"
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-2 pt-2 px-4">
                  <Button variant="outline" className="rounded-full w-full" asChild onClick={() => setMobileMenuOpen(false)}>
                    <Link to="/login">{t('nav.login')}</Link>
                  </Button>
                  <Button className="rounded-full w-full" asChild onClick={() => setMobileMenuOpen(false)}>
                    <Link to="/register">{t('nav.register')}</Link>
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
